import * as Sentry from '@sentry/vue'
import { defineStore } from 'pinia'

import api from '@/api/api'
import { useSettingStore } from '@/stores/setting'
import { byId, readCookie } from '@/utils/utils'

import { UserGroup, UserGroupType, UserRead } from '@generated/types'

export interface StoreUser {
  id: number
  email?: string
  first_name?: string
  last_name?: string
  name: string
  phone?: string | null
  groups: UserGroupType[]
}

export interface UserState {
  loading: Promise<boolean>
  users: StoreUser[]
  me: StoreUser & UserRead
}

function parseUser(user: UserRead): StoreUser {
  return {
    ...user,
    name: user.first_name + ' ' + user.last_name,
  }
}

export const useUserStore = defineStore({
  id: 'user',
  state: (): UserState => {
    const state: UserState = {
      loading: Promise.resolve(false),
      users: [],
      me: {
        id: 0,
        email: '',
        first_name: '',
        last_name: '',
        name: '',
        phone: '',
        groups: [],
        accepts_marketing: false,
        venues: [],
        hubspot_token: '',
      },
    }
    if (readCookie('tt_user')) {
      state.loading = api
        .getMe()
        .then(response => {
          const store = useUserStore()
          store.$patch({ me: parseUser(response.data) })
          if (import.meta.env.VUE_APP_VERSION) {
            Sentry.setUser({
              id: store.me.id + '',
              email: store.me.email,
              username: store.me.email,
            })
            window.dataLayer.push({
              user_id: store.me.id,
            })
            window.hsConversationsSettings.identificationEmail = store.me.email
            window.hsConversationsSettings.identificationToken = store.me.hubspot_token
            if (window.HubSpotConversations && window.HubSpotConversations.widget) {
              window.HubSpotConversations.widget.load()
            } else {
              window.hsConversationsSettings.loadImmediately = true
            }
          }
          return true
        })
        .catch(reason => {
          Sentry.captureException(reason)
          return false
        })
    }
    return state
  },
  getters: {
    isLoggedIn(): boolean {
      return !!readCookie('tt_user')
    },
    isOwner(): boolean {
      return this.me.groups.includes(UserGroup.OWNERS)
    },
    userById() {
      return (id: number) => {
        this.users.find(byId(id))
      }
    },
    hasGroup() {
      return (group: UserGroupType) => {
        return useUserStore().me.groups.includes(group)
      }
    },
    isSuperuser(): boolean {
      return this.me.email.endsWith('@turntabletickets.com')
    },
    initials(): string {
      return (this.me.first_name.charAt(0) || '') + (this.me.last_name.charAt(0) || '')
    },
  },
  actions: {
    async fetchUsers(): Promise<StoreUser[]> {
      const response = await api.getUsers()
      this.users = response.data.map(parseUser)
      return this.users
    },
    async hotjarIdentification() {
      if (window.hj && this.isLoggedIn) {
        const settingStore = useSettingStore()
        await Promise.all([settingStore.loading, this.loading])
        window.hj('identify', this.me.id.toString(), {
          email: this.me.email,
          venue: settingStore.venue_slug,
        })
      }
    },
  },
})
